import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, Check, Compass, Copy, Download, Mic, MicOff, RotateCcw, RotateCw, Send, Square, Table2, Volume2 } from "lucide-react";
import { toast } from "../common/Toast/toast";
import { useAuth } from "../../../context/AuthContext";
import { askAiBoard, detectAiBoardCollections, getAiBoardOptions } from "../../../services/AiBoardService";
import NewCustomAsyncSelectDropdown from "../common/NewCustomAsyncSelectDropdown.jsx";
import ExportDialog from "../common/Export/ExportDialog";
import AiBoardChart from "./AiBoardChart.jsx";
import AiBoardTour, { TOUR_SEEN_KEY } from "./AiBoardTour.jsx";
import "../Chat/ChatLayout.css";
import "../Chat/Chat.css";
import "../common/lms-pages.css";
import "./AiBoard.css";

/* The AI Board: staff ask about the LMS data in plain words. The AI is
   sent the question and the schemas of the collections picked here --
   no data -- and writes query plans; the API builds each into a MongoDB
   pipeline, checks and runs it (modules/aiboard) and the rows come
   back. This page draws each result as a table with its plan and
   pipeline folded beneath it.

   The thread is the assistant chat's (Chat.jsx assistant mode): the
   same canvas, bubbles and composer, without the list of conversations
   beside it. In the chat box, under the question: the Groq model, and
   the collections a question may use. Both lists come from
   GET /ai-board/options; the picks are kept in this browser.

   The collections are also picked for the reader: as the question is
   typed, the API reads which collections it names (POST
   /ai-board/detect -- names and everyday words, misspellings allowed)
   and those are added to the picker, where the reader sees them and can
   take one off or add another before sending. What was used carries on
   to the next question, so a follow-up needs no picking.

   A walkthrough (AiBoardTour) shows what each control does: by itself
   on the first visit, and from the Guide button any time.

   Under each answer: Listen (the browser's own speech, reading the
   answer and its first rows), Ask again (the same question, on the same
   collections), Download (a result, through the house export dialog --
   CSV or Excel; one button per result when there are several) and Copy
   (the answer as text, tables tab-separated so they paste as columns).
   In the chat box, beside the question: "Chart" -- ticked, each answer
   also comes as a chart where its rows make one (the API picks and
   works the chart out; AiBoardChart draws it), with the table one
   switch away. Before Send: a microphone -- the browser's own
   speech recognition types what is said into the question box. Behind the `ai-board` module grant (view), the same
   grant its API routes check. */

// Each suggestion picks the collections it needs before it is asked.
const SUGGESTIONS = [
  { q: "How many batches are there?", c: ["lms.batches"] },
  { q: "How many students are there?", c: ["billing.students"] },
  { q: "How many trainers are there?", c: ["lms.trainers"] },
  { q: "How many courses are there?", c: ["lms.courses"] },
  { q: "How many classrooms are there?", c: ["lms.classrooms"] },
  { q: "How many branches are there?", c: ["billing.branches"] },
];

const SHOWN_ROWS = 200;
// The chat's least height, on a short window.
const MIN_CHAT_HEIGHT = 420;

// The reader's last picks, kept in this browser. Storage can be missing
// or blocked; the page then starts from the defaults.
const PICKS_KEY = "aiBoard.picks";
const readPicks = () => {
  try {
    return JSON.parse(localStorage.getItem(PICKS_KEY) || "null");
  } catch (e) {
    return null;
  }
};
const savePicks = (picks) => {
  try {
    localStorage.setItem(PICKS_KEY, JSON.stringify(picks));
  } catch (e) {
    // Not kept; nothing else depends on it.
  }
};

const timeOf = (t) => (t ? new Date(t).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");

/* The greeting at the top of a conversation: "Good <time of day>,
   <first name>". The person is addressed by their own first name, as
   their login has it (Chat.jsx reads it the same way) -- never a
   nickname, never a title guessed from the name. No name, no address:
   just "Good evening". The time of day is the reader's clock. */
const firstNameOf = () => {
  try {
    const name = JSON.parse(localStorage.getItem("userDetails") || "{}").firstName;
    return typeof name === "string" && name.trim() ? name.trim() : null;
  } catch (e) {
    return null;
  }
};

// 5-12 morning, 12-17 afternoon, 17-21 evening, otherwise night.
const partOfDay = (hour) => (hour >= 5 && hour < 12 ? "morning" : hour >= 12 && hour < 17 ? "afternoon" : hour >= 17 && hour < 21 ? "evening" : "night");

const greeting = () => {
  const now = new Date();
  return {
    role: "assistant",
    isGreeting: true,
    name: firstNameOf(),
    partOfDay: partOfDay(now.getHours()),
    time: now,
  };
};

const unique = (list) => [...new Set(list)];

// How long typing pauses before the question is read for collections.
const DETECT_DELAY_MS = 400;

// A cell as text: lists and objects compactly, nothing as a dash.
const cellText = (v) => {
  if (v == null || v === "") return "—";
  if (typeof v === "object") {
    const s = JSON.stringify(v);
    return s.length > 140 ? `${s.slice(0, 140)}…` : s;
  }
  return String(v);
};

// The columns of a result: every key, in the order first seen.
const columnsOf = (rows) => {
  const cols = [];
  rows.slice(0, 50).forEach((r) => Object.keys(r || {}).forEach((k) => !cols.includes(k) && cols.push(k)));
  return cols;
};

/* ── Listening ─────────────────────────────────────────────────
   The browser reads an answer aloud (speechSynthesis). Tables do not
   read well, so a result is said as its title, its size and its first
   rows, each as "column value" pairs. */
const canSpeak = typeof window !== "undefined" && "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;
const SPOKEN_ROWS = 5;

// "avgPresent" -> "avg present", "trainer_name" -> "trainer name".
const asWords = (key) =>
  String(key)
    .replace(/[_.]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase();

const spokenValue = (v) => {
  if (v == null || v === "") return "none";
  if (typeof v === "object") return Array.isArray(v) ? v.join(", ") : "a value";
  return String(v);
};

const spokenOf = (msg) => {
  const parts = [];
  if (msg.isLimit) parts.push("Limit reached.");
  if (msg.message) parts.push(msg.message);
  (msg.queries || []).forEach((q) => {
    parts.push(`${q.label}.`);
    if (q.error) {
      parts.push("This query could not run.");
      return;
    }
    const rows = q.rows || [];
    if (!rows.length) {
      parts.push("No rows match.");
      return;
    }
    parts.push(`${rows.length}${q.truncated ? " or more" : ""} row${rows.length === 1 ? "" : "s"}.`);
    rows.slice(0, SPOKEN_ROWS).forEach((r, i) => {
      const pairs = Object.entries(r).map(([k, v]) => `${asWords(k)} ${spokenValue(v)}`);
      parts.push(`${rows.length > 1 ? `${i + 1}: ` : ""}${pairs.join(", ")}.`);
    });
    if (rows.length > SPOKEN_ROWS) parts.push(`And ${rows.length - SPOKEN_ROWS} more in the table.`);
  });
  return parts.join(" ");
};

/* ── Speaking a question ───────────────────────────────────────
   The browser's speech recognition (Chrome and Edge as
   webkitSpeechRecognition; Firefox has none, and the button is not
   shown there). What is heard is typed into the question box as it
   comes, after whatever was typed before; nothing is sent until Send. */
const Recognition = typeof window !== "undefined" ? window.SpeechRecognition || window.webkitSpeechRecognition : null;

/* ── Copying ───────────────────────────────────────────────────
   An answer as text: its message, then each result's title and its rows
   with tabs between the cells -- pasted into a spreadsheet, every cell
   lands in its own column. */
const copyCell = (v) => (v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v)).replace(/[\t\r\n]+/g, " ");

const copyTextOf = (msg) => {
  const parts = [];
  if (msg.question) parts.push(`Q: ${msg.question}`);
  if (msg.message) parts.push(msg.message);
  (msg.queries || []).forEach((q) => {
    const rows = q.rows || [];
    const lines = [q.label];
    if (q.error) lines.push(`This query could not run: ${q.error}`);
    else if (!rows.length) lines.push("No rows match.");
    else {
      const cols = columnsOf(rows);
      lines.push(cols.join("\t"), ...rows.map((r) => cols.map((c) => copyCell(r[c])).join("\t")));
    }
    parts.push(lines.join("\n"));
  });
  return parts.join("\n\n");
};

// The clipboard, or -- where it is refused (an http page, an old
// browser) -- a hidden text box copied the old way.
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    const box = document.createElement("textarea");
    box.value = text;
    box.setAttribute("readonly", "");
    box.style.position = "fixed";
    box.style.opacity = "0";
    document.body.appendChild(box);
    box.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch (err) {
      ok = false;
    }
    document.body.removeChild(box);
    return ok;
  }
};

// An assistant turn as the API is told it next time: the plans (or raw
// stages) it wrote, so a follow-up can change them.
const asHistory = (m) =>
  m.role === "user"
    ? { role: "user", content: m.content }
    : {
        role: "assistant",
        content: JSON.stringify({
          queries: (m.queries || []).map(({ label, plan, raw }) => ({ label, plan: plan || null, raw: raw || null })),
          message: m.message || null,
        }),
      };

/* ── While it works ────────────────────────────────────────────
   The wait is shown as what is going on, a phrase at a time: it opens
   on understanding the question, then moves through the rest in a
   shuffled order -- a new one every couple of seconds, never the same
   one twice running. Each fades in and carries a shimmer. */
const THINKING = [
  "Analysing the database",
  "Reading the collection schemas",
  "Musing",
  "Planning the query",
  "Joining the collections",
  "Crunching the numbers",
  "Pondering",
  "Sorting the results",
  "Connecting the dots",
  "Double-checking the plan",
  "Consulting the records",
  "Almost there",
];
const THINKING_FIRST = "Understanding your question";
const THINKING_EVERY_MS = 2200;

const Thinking = () => {
  const [phrase, setPhrase] = useState(THINKING_FIRST);
  useEffect(() => {
    const timer = setInterval(() => {
      setPhrase((current) => {
        const others = THINKING.filter((t) => t !== current);
        return others[Math.floor(Math.random() * others.length)];
      });
    }, THINKING_EVERY_MS);
    return () => clearInterval(timer);
  }, []);
  // key: a new phrase is a new element, so its fade-in plays again.
  return (
    <span key={phrase} className="aib-thinking" role="status">
      {phrase}…
    </span>
  );
};

const Result = ({ q, dbLabel }) => {
  const rows = useMemo(() => q.rows || [], [q.rows]);
  const cols = useMemo(() => columnsOf(rows), [rows]);
  // A result with a chart opens on it; the table is one switch away.
  const [view, setView] = useState(q.chart ? "chart" : "table");
  const showChart = q.chart && view === "chart";
  return (
    <section className="aib-result">
      <header className="aib-result-head">
        <strong>{q.label}</strong>
        <small>
          {q.collection} · {dbLabel}
          {!q.error && ` · ${rows.length}${q.truncated ? "+" : ""} row${rows.length === 1 ? "" : "s"}`}
        </small>
        {q.chart && !q.error && (
          <div className="aib-view" role="group" aria-label="Show as">
            <button type="button" className={view === "chart" ? "is-on" : ""} aria-pressed={view === "chart"} onClick={() => setView("chart")}>
              <BarChart3 size={13} aria-hidden="true" /> Chart
            </button>
            <button type="button" className={view === "table" ? "is-on" : ""} aria-pressed={view === "table"} onClick={() => setView("table")}>
              <Table2 size={13} aria-hidden="true" /> Table
            </button>
          </div>
        )}
      </header>

      {q.error ? (
        <p className="aib-result-error">This query could not run: {q.error}</p>
      ) : rows.length === 0 ? (
        <p className="aib-result-none">No rows match.</p>
      ) : showChart ? (
        <>
          <AiBoardChart chart={q.chart} title={q.label} />
          {q.chart.note && <p className="aib-result-none">{q.chart.note}</p>}
        </>
      ) : rows.length === 1 && cols.length <= 4 ? (
        /* One row of a few values -- "how many batches" -- is the answer
           itself: each value large, its column under it. */
        <div className="aib-figures">
          {cols.map((c) => (
            <div key={c} className="aib-figure">
              <span className="aib-figure-value">{cellText(rows[0][c])}</span>
              <span className="aib-figure-label">{asWords(c)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="chat-md aib-table">
          <table>
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c}>{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, SHOWN_ROWS).map((r, i) => (
                <tr key={i}>
                  {cols.map((c) => (
                    <td key={c}>{cellText(r[c])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.length > SHOWN_ROWS && <p className="aib-result-none">Showing the first {SHOWN_ROWS} of {rows.length} rows.</p>}
      {q.truncated && <p className="aib-result-none">Only the first 500 rows were returned.</p>}

      <details className="aib-steps">
        <summary>Query</summary>
        {q.plan && (
          <>
            <p className="aib-query-label">Plan (written by the AI)</p>
            <pre className="aib-query">{JSON.stringify(q.plan, null, 2)}</pre>
          </>
        )}
        {q.pipeline && (
          <>
            <p className="aib-query-label">{q.plan ? "Pipeline (built from the plan, as run)" : "Pipeline (as run)"}</p>
            <pre className="aib-query">{JSON.stringify(q.pipeline, null, 2)}</pre>
          </>
        )}
      </details>
    </section>
  );
};

const AiBoard = () => {
  const navigate = useNavigate();
  const { hasPermission, isInitialized, loading } = useAuth();
  // Admins and superadmins pass without a grant (hasPermission). It
  // answers "no" while the login is still loading, so nothing is decided
  // until it has -- an admin opening /ai-board directly is not sent away.
  const authReady = isInitialized && !loading;
  const allowed = authReady && hasPermission("ai-board", "view", "lms");
  const warned = useRef(false);
  const [messages, setMessages] = useState(() => [greeting()]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [options, setOptions] = useState(null);
  const [model, setModel] = useState("");
  // "Chart" ticked: answers also come as charts.
  const [visualize, setVisualize] = useState(false);
  // The walkthrough.
  const [touring, setTouring] = useState(false);
  /* The picker's collections ("lms.batches", ...) are three lists:
     `manual` -- picked by the reader, or used by the last question;
     `auto` -- found in the question being typed, [{ key, word }];
     `dismissed` -- found, but taken off by the reader, for this question. */
  const [manual, setManual] = useState([]);
  const [auto, setAuto] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const detectSeq = useRef(0);
  const detectedFor = useRef("");
  const canvasRef = useRef(null);
  const inputRef = useRef(null);
  const chatAppRef = useRef(null);

  /* The chat runs to the bottom of the window. Measured, as Chat.jsx
     measures its panel: the distance from the top of the document to
     the chat is the header, the shell's bar and the page title, at
     whatever height they are; the phone's fixed bottom bar comes off. */
  useLayoutEffect(() => {
    const panel = chatAppRef.current;
    if (!panel) return undefined;
    const fit = () => {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const above = panel.getBoundingClientRect().top + window.scrollY;
      const bar = document.querySelector(".smt-tb-right");
      const below = bar && window.getComputedStyle(bar).position === "fixed" ? bar.getBoundingClientRect().height : 0;
      panel.style.height = `${Math.max(MIN_CHAT_HEIGHT, Math.round(viewportHeight - above - below))}px`;
    };
    fit();
    const viewport = window.visualViewport;
    window.addEventListener("resize", fit);
    viewport?.addEventListener("resize", fit);
    return () => {
      window.removeEventListener("resize", fit);
      viewport?.removeEventListener("resize", fit);
    };
  }, [allowed]);
  // The answer being read aloud, by its place in the thread.
  const [speakingAt, setSpeakingAt] = useState(null);
  // The microphone: listening or not, and the text typed before it began.
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  const toggleListening = () => {
    if (!Recognition) return;
    if (listening) {
      stopListening();
      return;
    }
    stopSpeaking();
    const before = draft.trim();
    const recognition = new Recognition();
    recognition.lang = "en-IN";
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.onresult = (event) => {
      const heard = Array.from(event.results).map((r) => r[0].transcript).join(" ").trim();
      setDraft([before, heard].filter(Boolean).join(" "));
    };
    recognition.onerror = (event) => {
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        toast.error("The microphone is blocked for this site. Allow it in the browser to speak a question.");
      } else if (event.error !== "no-speech" && event.error !== "aborted") {
        toast.error("Could not hear that. Try again.");
      }
    };
    recognition.onend = () => {
      setListening(false);
      recognitionRef.current = null;
      inputRef.current?.focus();
    };
    recognitionRef.current = recognition;
    setListening(true);
    recognition.start();
  };

  // Nothing keeps listening after the page is left.
  useEffect(() => () => recognitionRef.current?.abort(), []);

  // The result in the export dialog, and the answer just copied.
  const [exporting, setExporting] = useState(null);
  const [copiedAt, setCopiedAt] = useState(null);
  const copiedTimer = useRef(null);

  const copyAnswer = async (index, msg) => {
    if (!(await copyToClipboard(copyTextOf(msg)))) {
      toast.error("The answer could not be copied.");
      return;
    }
    setCopiedAt(index);
    clearTimeout(copiedTimer.current);
    copiedTimer.current = setTimeout(() => setCopiedAt(null), 2000);
  };
  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const stopSpeaking = useCallback(() => {
    if (canSpeak) window.speechSynthesis.cancel();
    setSpeakingAt(null);
  }, []);

  // Read one answer; a second press, or another answer, stops it first.
  const speak = (index, msg) => {
    if (!canSpeak) return;
    const wasThis = speakingAt === index;
    window.speechSynthesis.cancel();
    if (wasThis) {
      setSpeakingAt(null);
      return;
    }
    const utterance = new window.SpeechSynthesisUtterance(spokenOf(msg));
    const voices = window.speechSynthesis.getVoices();
    utterance.voice = voices.find((v) => v.lang === "en-IN") || voices.find((v) => v.lang?.startsWith("en")) || null;
    utterance.rate = 1;
    utterance.onend = () => setSpeakingAt((at) => (at === index ? null : at));
    utterance.onerror = () => setSpeakingAt((at) => (at === index ? null : at));
    setSpeakingAt(index);
    window.speechSynthesis.speak(utterance);
  };

  // Nothing keeps talking after the page is left.
  useEffect(() => stopSpeaking, [stopSpeaking]);

  useEffect(() => {
    if (!authReady) return;
    if (!allowed && !warned.current) {
      warned.current = true;
      toast.error("You do not have permission to use the AI Board.");
      navigate("/");
    }
  }, [authReady, allowed, navigate]);

  // The pickers' lists, and the last picks kept here that still exist.
  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    getAiBoardOptions()
      .then((data) => {
        if (cancelled || !data) return;
        const kept = readPicks() || {};
        const keys = data.collections.map((c) => c.key);
        setOptions(data);
        setModel(data.models.some((m) => m.id === kept.model) ? kept.model : data.defaultModel);
        setVisualize(kept.visualize === true);
        setManual((kept.collections || []).filter((k) => keys.includes(k)));
      })
      .catch(() => {
        if (!cancelled) toast.error("The AI Board's models and collections could not be loaded.");
      });
    return () => {
      cancelled = true;
    };
  }, [allowed]);

  useEffect(() => {
    if (options) savePicks({ model, collections: manual, visualize });
  }, [options, model, manual, visualize]);

  // The first visit opens the guide, once the chat box is drawn.
  useEffect(() => {
    if (!options) return undefined;
    let seen = true;
    try {
      seen = localStorage.getItem(TOUR_SEEN_KEY) === "1";
    } catch (e) {
      seen = true;
    }
    if (seen) return undefined;
    const timer = setTimeout(() => setTouring(true), 400);
    return () => clearTimeout(timer);
  }, [options]);

  const picked = useMemo(
    () => unique([...manual, ...auto.map((a) => a.key).filter((k) => !dismissed.includes(k))]),
    [manual, auto, dismissed]
  );

  // Read the question for collections once typing pauses. A reply for
  // text that has since changed is dropped.
  const detect = async (text) => {
    const seq = ++detectSeq.current;
    try {
      const found = await detectAiBoardCollections(text);
      if (seq !== detectSeq.current) return null;
      detectedFor.current = text;
      setAuto(found);
      return found;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    if (!options) return undefined;
    const text = draft.trim();
    if (text.length < 3) {
      detectSeq.current += 1;
      detectedFor.current = text;
      setAuto([]);
      return undefined;
    }
    if (text === detectedFor.current) return undefined;
    const timer = setTimeout(() => detect(text), DETECT_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, options]);

  // The reader's edit in the picker: what they add is theirs; what they
  // take off stays off for this question, even if the question names it.
  const changePicks = (keys) => {
    const added = keys.filter((k) => !picked.includes(k));
    const removed = picked.filter((k) => !keys.includes(k));
    setManual((list) => unique([...list.filter((k) => !removed.includes(k)), ...added]));
    setDismissed((list) => unique([...list.filter((k) => !added.includes(k)), ...removed]));
  };

  const choices = useMemo(
    () => (options?.collections || []).map((c) => ({ value: c.key, label: `${c.collection} · ${c.dbLabel}` })),
    [options]
  );
  const dbLabelOf = useMemo(() => new Map((options?.collections || []).map((c) => [c.key, c.dbLabel])), [options]);

  // The thread follows the newest turn.
  useEffect(() => {
    const el = canvasRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const ask = async (text, given, asked = {}) => {
    const charts = typeof asked.visualize === "boolean" ? asked.visualize : visualize;
    const question = String(text ?? draft).trim();
    if (!question || sending) return;
    if (listening) stopListening();

    // The collections for this question: as picked, with the question read
    // once more when the last reading was of other text.
    let collections = given || picked;
    if (!given && detectedFor.current !== question) {
      const found = (await detect(question)) || [];
      collections = unique([...manual, ...found.map((a) => a.key).filter((k) => !dismissed.includes(k))]);
    }

    // The greeting and errors were not the AI's answers.
    const history = messages.filter((m) => !m.isError && !m.isGreeting).map(asHistory);

    setMessages((list) => [...list, { role: "user", content: question, time: new Date() }]);
    setDraft("");
    setSending(true);
    // What this question uses stays picked for the next one.
    setManual(collections);
    setAuto([]);
    setDismissed([]);
    detectedFor.current = "";
    try {
      const result = await askAiBoard(question, history, { model, collections, visualize: charts });
      // The API picks them itself when none were sent; show what it used.
      if (result?.collections?.length) setManual(result.collections);
      setMessages((list) => [
        ...list,
        {
          role: "assistant",
          question,
          visualize: charts,
          collections: result?.collections?.length ? result.collections : collections,
          message: result?.message,
          queries: result?.queries || [],
          model: result?.model,
          time: new Date(),
        },
      ]);
    } catch (error) {
      const data = error?.response?.data;
      setMessages((list) => [
        ...list,
        {
          role: "assistant",
          question,
          visualize: charts,
          collections,
          isError: true,
          isLimit: Boolean(data?.data?.limitReached),
          message:
            data?.message?.message ||
            (error?.code === "ECONNABORTED"
              ? "That took too long to answer. Try a narrower question."
              : "The AI Board could not answer just now. Try again in a moment."),
          time: new Date(),
        },
      ]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const askSuggestion = (s) => {
    const keys = (options?.collections || []).map((c) => c.key);
    ask(s.q, unique([...picked, ...s.c.filter((k) => keys.includes(k))]));
  };

  // The collections the question being typed named, for the note under the picker.
  const autoShown = auto.filter((a) => !dismissed.includes(a.key) && !manual.includes(a.key));
  const nameOf = (key) => choices.find((c) => c.value === key)?.label || key;

  if (!allowed) return null;

  return (
    <div className="pg lp aib-page">
      <header className="pg-head aib-head">
        <div className="pg-head-main">
          <h4 className="pg-title">AI Board</h4>
          <p className="pg-sub">Ask about the data in the selected collections. The AI writes the query; the server runs it.</p>
        </div>
        <div className="pg-head-actions">
          <button type="button" className="pg-btn" onClick={() => setTouring(true)} disabled={!options} title="A short guide to every control">
            <Compass size={15} aria-hidden="true" /> Guide
          </button>
          <button
            type="button"
            className="pg-btn aib-new"
            onClick={() => {
              stopSpeaking();
              setMessages([greeting()]);
              setDraft("");
            }}
            disabled={sending || messages.length <= 1}
          >
            <RotateCcw size={15} aria-hidden="true" /> New conversation
          </button>
        </div>
      </header>

      <AiBoardTour open={touring} onClose={() => setTouring(false)} />

      <ExportDialog
        open={Boolean(exporting)}
        onClose={() => setExporting(null)}
        title={exporting?.q.label}
        columns={exporting ? columnsOf(exporting.q.rows || []).map((c) => ({ key: c, label: c })) : []}
        rows={exporting?.q.rows || []}
        scopeNote={exporting ? `All ${exporting.q.rows.length}${exporting.q.truncated ? " (the first 500)" : ""} rows of this result.` : ""}
        filenameBase={`ai-board-${exporting?.q.label || "result"}`}
        sheetName={exporting?.q.label}
        preamble={exporting ? [exporting.q.label, `From ${exporting.q.collection} (${exporting.dbLabel}), ${new Date().toLocaleString()}`] : []}
      />

      <div className="chat-app aib-app" ref={chatAppRef}>
        <div className="chat-shell aib-shell">
          <section className="chat-main">
            <div className="chat-canvas" ref={canvasRef} aria-live="polite">
              {messages.map((msg, index) => {
                const mine = msg.role === "user";
                if (msg.isGreeting) {
                  // The greeting opens an empty conversation; once a
                  // question is asked the thread is the conversation.
                  if (messages.length > 1) return null;
                  return (
                    <header key={`aib-${index}`} className="aib-hero">
                      <h2 className="aib-hero-title">
                        Good {msg.partOfDay}
                        {msg.name && (
                          <>
                            , <span className="aib-hero-name">{msg.name}</span>
                          </>
                        )}
                      </h2>
                    </header>
                  );
                }
                return (
                  <div key={`aib-${index}`} className={`chat-msg${mine ? " is-mine" : ""}`}>
                    <div className={`chat-msg-col${msg.queries?.length ? " aib-col-wide" : ""}`}>
                      <div className="chat-msg-line">
                        <div className={`chat-bubble${mine ? " is-mine" : ""}${msg.isError ? " is-error" : ""}`}>
                          {mine ? (
                            <p className="chat-text">{msg.content}</p>
                          ) : (
                            <>
                              {msg.isLimit && <strong className="aib-limit">Limit reached</strong>}
                              {msg.message && <p className="chat-text">{msg.message}</p>}
                              {msg.queries?.map((q, i) => (
                                <Result key={i} q={q} dbLabel={dbLabelOf.get(q.key) || q.database} />
                              ))}
                              {!msg.message && !msg.queries?.length && <p className="chat-text">No query came back.</p>}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="chat-msg-meta">
                        <small className="chat-msg-time">
                          {timeOf(msg.time)}
                          {msg.model ? ` · ${msg.model.split("/").pop()}` : ""}
                        </small>
                      </div>
                      {!mine && !msg.isGreeting && (
                        <div className="aib-acts">
                          {canSpeak && (
                            <button
                              type="button"
                              className={`pg-btn aib-act${speakingAt === index ? " is-on" : ""}`}
                              onClick={() => speak(index, msg)}
                              aria-pressed={speakingAt === index}
                            >
                              {speakingAt === index ? <Square size={13} aria-hidden="true" /> : <Volume2 size={14} aria-hidden="true" />}
                              {speakingAt === index ? "Stop" : "Listen"}
                            </button>
                          )}
                          {msg.question && (
                            <button
                              type="button"
                              className="pg-btn aib-act"
                              onClick={() => ask(msg.question, msg.collections?.length ? msg.collections : undefined, { visualize: msg.visualize })}
                              disabled={sending}
                              title={`Ask "${msg.question}" again`}
                            >
                              <RotateCw size={14} aria-hidden="true" /> Ask again
                            </button>
                          )}
                          {(() => {
                            const downloadable = (msg.queries || []).filter((q) => q.rows?.length);
                            return downloadable.map((q, i) => (
                              <button
                                type="button"
                                key={`dl-${i}`}
                                className="pg-btn aib-act"
                                onClick={() => setExporting({ q, dbLabel: dbLabelOf.get(q.key) || q.database })}
                                title={`Download "${q.label}"`}
                              >
                                <Download size={14} aria-hidden="true" />
                                {downloadable.length > 1 ? `Download ${i + 1}` : "Download"}
                              </button>
                            ));
                          })()}
                          {(msg.message || msg.queries?.length > 0) && (
                            <button
                              type="button"
                              className={`pg-btn aib-act${copiedAt === index ? " is-on" : ""}`}
                              onClick={() => copyAnswer(index, msg)}
                            >
                              {copiedAt === index ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
                              {copiedAt === index ? "Copied" : "Copy"}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {sending && (
                <div className="chat-msg">
                  <div className="chat-msg-col">
                    <div className="chat-msg-line">
                      <div className="chat-bubble aib-wait">
                        <div className="typing-indicator">
                          <span className="dot" />
                          <span className="dot" />
                          <span className="dot" />
                        </div>
                        <Thinking />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* The suggestions run right to left without end: the list
                  twice in a row, the second copy hidden from screen
                  readers and the keyboard so it is read and tabbed once. */}
              {messages.length <= 1 && !sending && options && (
                <div className="aib-marquee" aria-label="Suggested questions">
                  <div className="aib-marquee-track">
                    {[0, 1].map((copy) =>
                      SUGGESTIONS.map((s) => (
                        <button
                          type="button"
                          key={`${copy}-${s.q}`}
                          className="aib-chip"
                          onClick={() => askSuggestion(s)}
                          aria-hidden={copy === 1 ? "true" : undefined}
                          tabIndex={copy === 1 ? -1 : undefined}
                        >
                          {s.q}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="chat-composer">
              <form
                className="chat-composer-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  ask();
                }}
              >
                <div className="chat-input-pill">
                  <input
                    ref={inputRef}
                    type="text"
                    className="chat-input"
                    placeholder={listening ? "Listening… speak your question" : "Ask a question -- e.g. ongoing batches per course…"}
                    aria-label="Your question"
                    maxLength={2000}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    disabled={sending}
                  />
                  <label className={`aib-viz${visualize ? " is-on" : ""}`} title="Answer with charts where the data makes one">
                    <input type="checkbox" checked={visualize} onChange={(e) => setVisualize(e.target.checked)} disabled={sending} />
                    <BarChart3 size={15} aria-hidden="true" />
                    <span>Chart</span>
                  </label>
                  {Recognition && (
                    <button
                      type="button"
                      className={`aib-mic${listening ? " is-on" : ""}`}
                      onClick={toggleListening}
                      disabled={sending}
                      aria-pressed={listening}
                      aria-label={listening ? "Stop listening" : "Speak your question"}
                      title={listening ? "Stop listening" : "Speak your question"}
                    >
                      {listening ? <MicOff size={17} aria-hidden="true" /> : <Mic size={17} aria-hidden="true" />}
                    </button>
                  )}
                  <button type="submit" className="chat-send" disabled={sending || !draft.trim()} aria-label="Ask">
                    <Send size={18} />
                  </button>
                </div>
              </form>
              {/* Under the question: what it is asked of -- the model, and
                  the collections it may use. */}
              <div className="aib-tools">
                {!options ? (
                  <span className="aib-tools-wait">Loading the models and collections...</span>
                ) : (
                  <>
                    <select
                      className="pg-select aib-model"
                      aria-label="AI model"
                      title="AI model"
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                      disabled={sending}
                    >
                      {options.models.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.label}
                          {m.id === options.defaultModel ? " (default)" : ""}
                        </option>
                      ))}
                    </select>
                    <div className="aib-colls" aria-label="Collections">
                      <NewCustomAsyncSelectDropdown
                        isMulti
                        isSearchable
                        options={choices}
                        value={choices.filter((c) => picked.includes(c.value))}
                        onChange={(val) => changePicks((val || []).map((v) => v.value))}
                        placeholder="Select collections..."
                        isDisabled={sending}
                        style={{ minHeight: "32px", borderRadius: "8px" }}
                      />
                    </div>
                  </>
                )}
              </div>
              {autoShown.length > 0 && (
                <p className="aib-auto" aria-live="polite">
                  Picked from your question:{" "}
                  {autoShown.map((a, i) => (
                    <span key={a.key}>
                      {i > 0 && ", "}
                      <strong>{nameOf(a.key)}</strong> <span className="aib-auto-word">("{a.word}")</span>
                    </span>
                  ))}
                  . Remove any that are wrong.
                </p>
              )}
              <p className="aib-warn">
                AI can make mistakes. Check important figures -- open "Query" under a result to see how it was worked out.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default AiBoard;
